import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import { useTeamContext } from '@/hooks/useTeamContext';
import { useDeferredReady } from '@/hooks/useDeferredReady';
import { useApiReady } from '@/hooks/useApiReady';
import { get } from '@/utils/https';
import {
  isIntercomVisitorRoute,
  shouldHideIntercom,
} from '@/lib/intercom/intercomRouteConfig';
import {
  applyIntercomHideStyle,
  applyIntercomElevatedZIndexStyle,
  INTERCOM_MESSENGER_Z_INDEX,
  isIntercomHelpOpen,
  removeIntercomHideStyle,
  removeIntercomElevatedZIndexStyle,
} from '@/lib/intercom/intercomVisibility';

function buildUserDisplayName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
}

/**
 * Intercom Messenger — monté au niveau router (main.jsx).
 * - Mode visiteur : /login sans session (support avant connexion)
 * - Mode authentifié : JWT + profil utilisateur
 */
export default function IntercomMessenger() {
  const { user } = useTeamContext();
  const apiReady = useApiReady();
  const intercomIdleReady = useDeferredReady(1000);
  const { i18n } = useTranslation();
  const location = useLocation();
  const { boot, update, hide } = useIntercom();
  const currentLanguage = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const [jwtToken, setJwtToken] = useState(null);
  const [jwtFetchAttempted, setJwtFetchAttempted] = useState(false);
  const fetchingJwtRef = useRef(false);
  const hasBootedRef = useRef(false);

  const pathname = location.pathname;
  const shouldHide = shouldHideIntercom(pathname);
  const isVisitorRoute = isIntercomVisitorRoute(pathname);
  const isVisitorMode = isVisitorRoute && !user?.id;

  // JWT uniquement pour les utilisateurs connectés (hors routes masquées)
  useEffect(() => {
    if (fetchingJwtRef.current) return;

    if (apiReady && intercomIdleReady && user?.id) {
      fetchingJwtRef.current = true;
      get('/api/app/v1/intercom/jwt', {}, { hideMessageModal: true })
        .then((response) => {
          if (response?.code === 200 && response?.data?.token) {
            setJwtToken(response.data.token);
          } else {
            setJwtToken(null);
          }
        })
        .catch(() => setJwtToken(null))
        .finally(() => {
          setJwtFetchAttempted(true);
          fetchingJwtRef.current = false;
        });
      return;
    }

    setJwtToken(null);
    setJwtFetchAttempted(false);
    fetchingJwtRef.current = false;
  }, [apiReady, intercomIdleReady, user, shouldHide]);

  // Boot visiteur sur /login (sans session)
  useEffect(() => {
    if (shouldHide || !isVisitorMode || !intercomIdleReady || hasBootedRef.current) {
      return;
    }

    try {
      boot({
        sessionDuration: 86400000,
        hideDefaultLauncher: false,
        languageOverride: currentLanguage,
      });
      hasBootedRef.current = true;
    } catch (error) {
      console.error('Failed to boot Intercom (visitor):', error);
    }
  }, [shouldHide, isVisitorMode, intercomIdleReady, boot, currentLanguage]);

  // Boot authentifié (après récupération JWT)
  useEffect(() => {
    if (isVisitorMode || !jwtFetchAttempted || !user?.id || hasBootedRef.current) {
      return;
    }

    const fullName = buildUserDisplayName(user);
    const bootProps = {
      sessionDuration: 86400000,
      hideDefaultLauncher: shouldHide,
      zIndex: INTERCOM_MESSENGER_Z_INDEX,
      languageOverride: currentLanguage,
      ...(fullName && { name: fullName }),
      customAttributes: {
        ...(jwtToken && { intercom_user_jwt: jwtToken }),
      },
    };

    try {
      boot(bootProps);
      hasBootedRef.current = true;
    } catch (error) {
      console.error('Failed to boot Intercom:', error);
    }
  }, [shouldHide, isVisitorMode, jwtFetchAttempted, jwtToken, user, boot, currentLanguage]);

  // Mise à jour profil / langue (session authentifiée)
  useEffect(() => {
    if (!hasBootedRef.current || shouldHide || !user?.id || !jwtToken) {
      return;
    }

    const fullName = buildUserDisplayName(user);
    const updateProps = {
      languageOverride: currentLanguage,
      ...(fullName && { name: fullName }),
      customAttributes: {
        intercom_user_jwt: jwtToken,
      },
    };

    try {
      update(updateProps);
    } catch (error) {
      console.error('Failed to update Intercom:', error);
    }
  }, [jwtToken, user, shouldHide, update, currentLanguage]);

  // Langue visiteur sur /login
  useEffect(() => {
    if (!hasBootedRef.current || shouldHide || !isVisitorMode) {
      return;
    }

    try {
      update({ languageOverride: currentLanguage });
    } catch {
      // Ignorer les erreurs
    }
  }, [currentLanguage, isVisitorMode, shouldHide, update]);

  // Visibilité launcher selon la route
  useEffect(() => {
    if (!hasBootedRef.current) {
      return;
    }

    if (shouldHide) {
      if (isIntercomHelpOpen()) {
        removeIntercomHideStyle();
        applyIntercomElevatedZIndexStyle();
        return;
      }

      hide();
      applyIntercomHideStyle();
      removeIntercomElevatedZIndexStyle();
      return;
    }

    removeIntercomHideStyle();
    removeIntercomElevatedZIndexStyle();
    try {
      update({ hideDefaultLauncher: false });
    } catch {
      // Ignorer les erreurs
    }
  }, [shouldHide, update, hide]);

  return null;
}
